import { useLocation, useNavigate } from "@solidjs/router";
import { activeElement, renderer } from "@solidtv/solid";
import { useAnnouncer, FPSCounter, setupFPS } from "@solidtv/solid/primitives";
import Background from "../components/Background";
import NavDrawer from "../components/NavDrawer/NavDrawer";
import EntityOverlay from "../components/EntityOverlay";
import PlayerOverlay from "../components/PlayerOverlay";
import ExitPrompt from "../components/ExitPrompt";
import { entityState, playerState, setEntityState, setPlayerState, exitPromptState, setExitPromptState } from "../overlayState";
import { createEffect, createSignal, onCleanup } from "solid-js";

declare module "@solidtv/solid" {
  // Augment the FocusManager KeyMap interface with our custom keys
  interface KeyMap {
    Announcer: (string | number)[];
    Menu: (string | number)[];
    Escape: (string | number)[];
    Backspace: (string | number)[];
  }
  interface ElementNode {
    heroContent?: boolean;
    backdrop?: any;
    entityInfo?: any;
    href?: string;
  }
}

const LeftNavWrapper = (props) => {
  const navigate = useNavigate();
  const announcer = useAnnouncer();
  announcer.debug = true;
  announcer.enabled = false;

  let navDrawer, lastFocused;

  function focusNavDrawer() {
    if (entityState() || playerState() || exitPromptState()) return false;
    if (navDrawer.states.has("focus")) {
      return false;
    }
    lastFocused = activeElement();
    return navDrawer.setFocus();
  }

  const [showWidgets, setShowWidgets] = createSignal(true);
  const location = useLocation();
  const showOnPaths = ["/home", "/live"];
  createEffect((prevPath) => {
    const currentPath = location.pathname;
    if (prevPath && prevPath !== currentPath) {
      setEntityState(null);
      setPlayerState(null);
    }
    let matchesPartial = showOnPaths.some((path) => currentPath.startsWith(path));
    if (currentPath === "/") {
      matchesPartial = true;
    }
    setShowWidgets(matchesPartial);
    return currentPath;
  }, location.pathname);

  const [lastKey, setLastKey] = createSignal<string>("Last key: undefined");
  const [lastError, setLastError] = createSignal<string | undefined>();
  const keyPressHandler = (e) => {
    setLastKey(`Last key: ${e.key}, Code: ${e.keyCode}`);
  };
  document.addEventListener("keydown", keyPressHandler);
  const displayError = (e) => {
    setLastError((p) => (p || "") + "\n" + e.message);
  };
  document.addEventListener("onerror", displayError);
  const windowSize = `${window.innerWidth}x${window.innerHeight}`;
  const windowSize2 = `${screen.width}x${screen.height}`;
  onCleanup(() => {
    document.removeEventListener("onerror", displayError);
    document.removeEventListener("keydown", keyPressHandler);
  });

  let pageContainer;

  function handleBack() {
    // If nav drawer is focused, close it by returning focus to the page
    if (navDrawer && navDrawer.states.has("focus")) {
      (lastFocused || pageContainer).setFocus();
      return true;
    }

    // Check if we are at the initial page (Home screen)
    if (location.pathname === '/' || location.pathname === '/home') {
      if (!exitPromptState()) {
        setExitPromptState(true);
      }
      return true;
    }

    // Otherwise, go back in history
    navigate(-1);
    return true;
  }

  return (
    <view
      ref={window.APP as any}
      onAnnouncer={() => (announcer.enabled = !announcer.enabled)}
      onLast={() => history.back()}
      onMenu={() => {
        if (entityState() || playerState() || exitPromptState()) return false;
        navigate("/");
      }}
      onBack={handleBack}
      onBackspace={handleBack}
      onEscape={handleBack}
      style={{ width: 1920, height: 1080 }}
      onLeft={focusNavDrawer}
      onRight={() => navDrawer.states.has("focus") && (lastFocused || pageContainer).setFocus()}
    >
      <Background />
      <NavDrawer ref={navDrawer} focusPage={() => lastFocused.setFocus()} showWidgets={showWidgets()} />
      <view id="pageContainer" ref={pageContainer} forwardFocus={0} children={props.children} />
      <EntityOverlay />
      <PlayerOverlay />
      <ExitPrompt onClose={() => pageContainer.setFocus()} />
    </view>
  );
};

export default LeftNavWrapper;


